import { create } from 'zustand';
import { usePermissionsStore } from './permissions-store';
import { isExpired, recordServerClock } from '../lib/jwt';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  /** @deprecated Use organization?.name. Kept for legacy callers. */
  organizationName?: string;
  organizationId?: string;
  organization?: { id: string; name: string; defaultLanguage?: string };
  role?: string;
  avatar?: string;
  /** Preferred UI language (BCP-47, e.g. "en" | "fr-CA"). Applied on login by useSyncUserLanguage. */
  language?: string;
  /** ATL-000440 — "Allow user to add manual date/timestamps" (V1
   *  users.update_geofence_date_stamp). Gates manual editing of the USPS
   *  Geofence In/Out pickers; the server enforces the matching completion
   *  gate. Populated by /bootstrap and /auth/me. */
  updateGeofenceDateStamp?: boolean;
  // ATL-000610 — "Allow user to update Geofence date stamp" (V1
  // users.permission_geofence_timestamp). Gates the Fix Timestamps GPS
  // backfill button; distinct from updateGeofenceDateStamp above.
  permissionGeofenceTimestamp?: boolean;
}

interface AuthStore {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  setUser: (user: User | null) => void;
  setAccessToken: (token: string | null) => void;
  setIsLoading: (loading: boolean) => void;
  logout: () => void;
  login: (user: User, accessToken: string) => void;
  switchOrg: (orgId: string, orgName: string, newToken: string) => void;
}

// Hydrate initial state from localStorage. If the persisted access token
// is already expired AND there is no refresh token to renew it with, treat
// the session as ended and clear the slot — this prevents a flash of the
// protected layout on cold load with a dead token.
//
// ATL-001144 (SEC-035): access tokens live 15 minutes, so an expired one on
// load is normal (a tab reopened after a break). With a refresh token the
// session is kept: SessionExpiryListener renews it straight away and any
// request that races it is renewed and retried by the axios interceptor.
function getPersistedState() {
  try {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken && isExpired(accessToken) && !localStorage.getItem('refreshToken')) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      return { user: null, accessToken: null, isAuthenticated: false };
    }
    const userJson = localStorage.getItem('user');
    const user = userJson ? JSON.parse(userJson) as User : null;
    return {
      user,
      accessToken,
      isAuthenticated: !!(accessToken && user),
    };
  } catch {
    return { user: null, accessToken: null, isAuthenticated: false };
  }
}

const persisted = getPersistedState();

export const useAuthStore = create<AuthStore>((set) => ({
  user: persisted.user,
  accessToken: persisted.accessToken,
  isAuthenticated: persisted.isAuthenticated,
  isLoading: persisted.isAuthenticated, // only loading if we think we have a session to verify

  setUser: (user) => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
    set({
      user,
      isAuthenticated: user !== null,
    });
  },

  setAccessToken: (token) => {
    if (token) {
      localStorage.setItem('accessToken', token);
    } else {
      localStorage.removeItem('accessToken');
    }
    set({
      accessToken: token,
    });
  },

  setIsLoading: (loading) =>
    set({
      isLoading: loading,
    }),

  logout: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    usePermissionsStore.getState().clear();
    set({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
    });
  },

  login: (user, accessToken) => {
    // ATL-001144: a just-issued token — learn the server clock from its iat.
    recordServerClock(accessToken);
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('user', JSON.stringify(user));
    set({
      user,
      accessToken,
      isAuthenticated: true,
    });
  },

  switchOrg: (orgId, orgName, newToken) => {
    localStorage.setItem('accessToken', newToken);
    set((state) => {
      const updatedUser = state.user
        ? {
            ...state.user,
            organizationId: orgId,
            organization: { id: orgId, name: orgName },
            organizationName: orgName,
          }
        : null;
      if (updatedUser) {
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
      return { user: updatedUser, accessToken: newToken };
    });
  },
}));
